<template>
    <div class="border-t-2 border-blue-600 mt-2 md:mt-4">
        <div class="tan-control-box">
            <div class="select-title pt-1 pb-1 print:hidden">Select:</div>
            <contract-selector
                v-model="symbol"
                :initial="initialSymbol"
                :all-contracts="allContracts">
            </contract-selector>

            <div class="inline-block w-full md:w-auto ml-2 mt-1 md:mt-0 mb-3 text-center print:hidden">
                <button class="btn btn-yellow btn-sm" @click="goTo">Show Spread Contracts</button>
            </div>
        </div>

        <alert-box v-if="Object.keys(spreadQuotes).length === 0 && !loading" body="Please select a contract"></alert-box>

        <div v-if="!loading">
            <div v-if="initialSymbol && initialSymbol.commodity" class="mt-2 text-lg font-medium flex">
                <span class="mt-3">{{ initialSymbol.commodity }} Spreads</span>
                <button @click="openFlipCharts" class="ml-auto items-center text-xs lg:text-sm btn btn-sm text-blue-600 hover:text-blue-400 mb-2 mt-0 sm:mt-2">
                    <span class="mr-1">FlipCharts</span>
                    <i class="fas fa-chart-area"></i>
                </button>
            </div>

            <div
                class="border-t-2 border-blue-600 mb-4"
                :key="spreadType"
                v-for="(spreadRows, spreadType) in spreadQuotes">

                <div class="table-header">
                    <div class="flex items-center text-base font-bold">
                        {{ getSpreadName(spreadType) }} Spreads
                        <span v-if="descriptions[spreadType]" class="text-blue-600 cursor-pointer ml-1 print:hidden" v-tooltip="{
                            content: descriptions[spreadType].description,
                            placement: 'auto-start',
                            theme: 'info',
                        }">
                            <i class="fa fa-info-circle"></i>
                        </span>
                    </div>
                </div>

                <data-table
                    :default-columns="getSpreadName(spreadType) === 'Other' ? otherColumns : columns"
                    :default-rows="spreadRows"
                    :default-meta="spreadQuotesMeta"
                >
                </data-table>
            </div>
        </div>
        <!-- Else we're loading -->
        <div v-else>
            <spinner></spinner>
        </div>
    </div>
</template>


<script setup>
import { ref, onMounted, nextTick } from 'vue';
import { useFlipcharts } from './DataTable/mixins/FlipCharts';

const props = defineProps({
    allContracts: Object,
    initialSymbol: Object,
    descriptions: {
        type: Object,
        required: true
    },
    columns: {
        type: Array,
        required: true
    }
});

const emit = defineEmits();
const { flipcharts } = useFlipcharts();

const symbol = ref(null);
const loading = ref(false);
const spreadQuotes = ref({});
const spreadQuotesMeta = ref({});
const otherColumns = ref(addSpreadTypeColumn(props.columns.slice(0)));

const getSpreadName = (spreadType) => {
    const spread = props.descriptions[spreadType];
    return spread ? spread.name : 'Other';
};

function addSpreadTypeColumn(columns) {
    columns.splice(2, 0, {
        field: 'spreadType',
        label: 'Type',
        sortable: false
    });
    return columns;
}

const goTo = () => {
    window.location = window.route('quotes.spread', { symbol: symbol.value });
};

const openFlipCharts = () => {
    let symbols = [];
    Object.keys(spreadQuotes.value).map((mSpreadType) => {
        symbols = symbols.concat(spreadQuotes.value[mSpreadType].map(mQuote => mQuote.symbol));
    });
    flipcharts(symbols);
};

const request = () => {
    const route = window.route('api.quotes');
    const params = {
        lists: 'futures.spreads_including_contract',
        baseSymbol: symbol.value,
        fields: 'symbol,symbolName,openPrice,highPrice,lowPrice,lastPrice,priceChange,volume,tradeTime,spreadType,leg1Expiration,leg2Expiration,leg3Expiration,leg4Expiration',
        groupBy: 'spreadType',
        orderBy: 'spreadType',
        orderDir: 'desc',
        raw: true,
        meta: 'field.type,field.name',
        after: 'addSpreadName',
    };

    loading.value = true;

    axios.post(route, params)
        .then((response) => {
            spreadQuotes.value = response.data.data;
            spreadQuotesMeta.value = response.data.meta;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
};

onMounted(() => {
    nextTick(() => {
        request();
    });
});
</script>
